import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ExternalLink, FileText, Paperclip, ShieldCheck } from 'lucide-react';
import {
  api,
  type BisScreenStatus,
  type RecordBisScreenResponse,
  type UploadBisScreenDocumentResponse,
} from '@/lib/api';
import { DocumentLink } from '@/components/document-link';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Customer public id (cus_…). */
  customerPublicId: string;
  customerLegalName: string;
  customerCountryCode: string | null;
  /** RFQ public id — used for cache invalidation so the chip re-renders. */
  rfqPublicId?: string;

  // Prior state (so the modal can show "last screened" + prefill notes).
  bisScreenStatus: BisScreenStatus | null;
  bisLastScreenedAt: string | null;
  bisScreenNotes: string | null;
  bisScreenPdfKey: string | null;
  bisScreenPdfUrl: string | null;
  bisScreenDocumentFilename: string | null;
  bisScreenDocumentSha256: string | null;
}

const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r !== 'string') return reject(new Error('Unexpected reader result'));
      const i = r.indexOf(',');
      resolve(i >= 0 ? r.slice(i + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * The trade.gov Consolidated Screening List search tool. This is a
 * client-rendered data-visualization widget, not a page that accepts a
 * `?name=`/`?countries=` query string — there is no supported way to
 * prefill it from a URL, so we link straight to the tool and the broker
 * types the customer name in themselves.
 *
 * Phase A is manual — the broker clicks through, reviews hits, decides.
 * Phase B (later) would call the CSL API directly and capture the result.
 */
const CSL_SEARCH_URL = 'https://www.trade.gov/data-visualization/csl-search';

const STATUS_LABEL: Record<BisScreenStatus, string> = {
  clear: 'Clear — no hits',
  flagged: 'Flagged — do not proceed',
  review_pending: 'Review pending',
};

const STATUS_DESC: Record<BisScreenStatus, string> = {
  clear: 'No matches on BIS Entity List, OFAC SDN, or other CSL sublists.',
  flagged: 'One or more hits that block the deal. Capture which list + entity in notes.',
  review_pending: 'Possible match needing more research (e.g., common name, need to confirm address).',
};

export function BisScreenModal({
  open,
  onClose,
  customerPublicId,
  customerLegalName,
  customerCountryCode,
  rfqPublicId,
  bisScreenStatus,
  bisLastScreenedAt,
  bisScreenNotes,
  bisScreenPdfKey,
  bisScreenPdfUrl,
  bisScreenDocumentFilename,
  bisScreenDocumentSha256,
}: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Default the form to the prior result so a re-screen is a small edit, not
  // a full re-entry. Notes prefill so the broker can append rather than retype.
  const [status, setStatus] = useState<BisScreenStatus>(bisScreenStatus ?? 'clear');
  const [notes, setNotes] = useState(bisScreenNotes ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStatus(bisScreenStatus ?? 'clear');
      setNotes(bisScreenNotes ?? '');
      setLocalError(null);
    }
  }, [open, bisScreenStatus, bisScreenNotes]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['customers'] });
    if (rfqPublicId) qc.invalidateQueries({ queryKey: ['rfq', rfqPublicId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  };

  const record = useMutation({
    mutationFn: () =>
      api<RecordBisScreenResponse>(`/api/customers/${customerPublicId}/bis-screen`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status, notes: notes.trim() || undefined }),
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fileBase64 = await fileToBase64(file);
      return api<UploadBisScreenDocumentResponse>(
        `/api/customers/${customerPublicId}/bis-screen-document`,
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          body: JSON.stringify({ fileBase64, fileName: file.name, mimeType: file.type }),
        },
      );
    },
    onSuccess: () => invalidate(),
  });

  function pickFile() {
    setLocalError(null);
    fileRef.current?.click();
  }
  function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_MIMES.has(file.type)) {
      setLocalError(`Unsupported file type "${file.type || 'unknown'}". Accepted: PDF, JPEG, PNG.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — limit is 10 MB.`);
      return;
    }
    upload.mutate(file);
  }

  const recordErr = record.isError ? (record.error as { message?: string })?.message ?? 'Save failed' : null;
  const uploadErr = upload.isError ? (upload.error as { message?: string })?.message ?? 'Upload failed' : null;
  const errMsg = localError ?? recordErr ?? uploadErr;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" {...dismiss}>
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[94dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" aria-hidden /> BIS / OFAC screening
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Manual screen against the trade.gov Consolidated Screening List (Entity List, OFAC SDN, etc.).
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-5">
          <div className="bg-muted/40 border border-border rounded p-3 text-sm">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium">{customerLegalName}</span>
              {customerCountryCode && (
                <span className="text-muted-foreground font-mono text-xs">· {customerCountryCode}</span>
              )}
            </div>
            {bisLastScreenedAt && (
              <div className="text-xs text-muted-foreground mt-1">
                Last screened {new Date(bisLastScreenedAt).toLocaleDateString()} — status was{' '}
                <span className="font-medium">{bisScreenStatus ? STATUS_LABEL[bisScreenStatus] : 'unknown'}</span>
              </div>
            )}
          </div>

          {/* Step 1 — open the CSL search tool for the broker to search by hand. */}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              1. Run the search
            </div>
            <a
              href={CSL_SEARCH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-sm font-medium rounded hover:opacity-90"
            >
              Open CSL search <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            </a>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Opens trade.gov in a new tab. Search for "{customerLegalName}"
              {customerCountryCode ? ` (${customerCountryCode})` : ''} and review every hit before recording a
              result below.
            </p>
          </div>

          {/* Step 2 — record the result. */}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              2. Record the result
            </div>
            <div className="space-y-2">
              {(['clear', 'flagged', 'review_pending'] as BisScreenStatus[]).map((s) => (
                <label
                  key={s}
                  className={`flex items-start gap-2 p-2.5 border rounded cursor-pointer ${
                    status === s ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="bis-status"
                    value={s}
                    checked={status === s}
                    onChange={() => setStatus(s)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{STATUS_LABEL[s]}</div>
                    <div className="text-[11px] text-muted-foreground">{STATUS_DESC[s]}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Step 3 — notes (encouraged for flagged / review_pending). */}
          <label className="block">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              3. Notes {(status === 'flagged' || status === 'review_pending') && <span className="normal-case tracking-normal text-amber-700">(strongly recommended)</span>}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                status === 'clear'
                  ? 'Optional. e.g., "No hits, screened against full CSL on 2026-05-27."'
                  : 'Required: which list, which entity, what action you took (e.g., "Flagged on OFAC SDN as cover for sanctioned entity — declined the deal").'
              }
              rows={3}
              maxLength={2000}
              className="w-full px-2 py-1.5 border border-border rounded text-sm"
            />
          </label>

          {/* Step 4 — optional screenshot/PDF upload for audit. */}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              4. Attach search-result document (optional)
            </div>
            {bisScreenPdfKey || bisScreenPdfUrl ? (
              <div className="flex items-center flex-wrap gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" aria-hidden />
                <DocumentLink
                  objectKey={bisScreenPdfKey}
                  fallbackUrl={bisScreenPdfUrl}
                  label={bisScreenDocumentFilename ?? 'BIS screen document'}
                  title={`${bisScreenDocumentFilename ?? 'BIS screen document'}${bisScreenDocumentSha256 ? `\nSHA-256: ${bisScreenDocumentSha256}` : ''}`}
                  className="text-primary hover:underline font-medium truncate max-w-[18rem]"
                />
                <button
                  type="button"
                  onClick={pickFile}
                  disabled={upload.isPending}
                  className="px-1.5 py-0.5 border border-border rounded hover:bg-muted disabled:opacity-50"
                >
                  {upload.isPending ? 'Uploading…' : 'Replace'}
                </button>
              </div>
            ) : (
              <div className="flex items-center flex-wrap gap-2 text-xs">
                <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden />
                <button
                  type="button"
                  onClick={pickFile}
                  disabled={upload.isPending}
                  className="px-1.5 py-0.5 border border-border rounded hover:bg-muted text-muted-foreground disabled:opacity-50"
                >
                  {upload.isPending ? 'Uploading…' : '+ Attach CSL search result (PDF/JPG/PNG)'}
                </button>
                <span className="text-[11px] text-muted-foreground italic">
                  Up to 10 MB. SHA-256 stored for tamper-evidence.
                </span>
              </div>
            )}
          </div>

          {errMsg && (
            <div className="flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {errMsg}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-border rounded hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => record.mutate()}
            disabled={record.isPending}
            className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
          >
            {record.isPending ? 'Saving…' : bisScreenStatus ? 'Re-record result' : 'Record result'}
          </button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={onPicked}
          className="hidden"
        />
      </div>
    </div>
  );
}
